import React, { createContext, useContext, useMemo } from "react";
import { ScrollableTable } from "@/components/admin/ScrollableTable";
import { useTableSelection, buildSelectionCsv, downloadCsv } from "@/lib/tableSelection";

const SelCtx = createContext(null);

// Reusable selection frame composed with ScrollableTable. Adds a visible, accessible
// selection bar (counter, "select all filtered", clear, non-destructive CSV export) and
// exposes row/header checkbox helpers via context. Works on read-only tables too.
export function SelectableTable({ rows, rowId, csvColumns, filename = "selection.csv",
  ariaLabel = "Tableau — sélection et défilement", className = "", extraActions = null, children }) {
  const { ids, allFilter, setAllFilter, clear, toggleRow, togglePage } = useTableSelection();
  const allIds = useMemo(() => rows.map(rowId), [rows, rowId]);
  const cols = useMemo(() => {
    if (csvColumns && csvColumns.length) return csvColumns;
    const r0 = rows[0] || {};
    return Object.keys(r0)
      .filter((k) => ["string", "number", "boolean"].includes(typeof r0[k]))
      .map((k) => ({ label: k, get: (r) => r[k] }));
  }, [csvColumns, rows]);
  const count = allFilter ? allIds.length : ids.size;
  const allPage = allIds.length > 0 && allIds.every((id) => ids.has(id));
  const indeterminate = !allPage && allIds.some((id) => ids.has(id));
  const exportCsv = () => {
    const sel = allFilter ? rows : rows.filter((r) => ids.has(rowId(r)));
    downloadCsv(buildSelectionCsv(sel, cols), filename);
  };
  const ctx = { isSel: (id) => allFilter || ids.has(id), toggleRow, allPage, indeterminate,
    togglePage: () => togglePage(allIds) };
  return (
    <div className="space-y-2" data-testid="selectable-table">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs" data-testid="selection-bar">
        <span className="font-semibold text-slate-600" data-testid="selection-count">{count} sélectionné(s){allFilter ? " (tout le filtre)" : ""}</span>
        {ids.size > 0 && !allFilter && allIds.length > ids.size && (
          <button onClick={() => setAllFilter(true)} data-testid="selection-all-filter" className="rounded border border-slate-300 px-2 py-1 font-medium text-[#1E3D2B]">Sélectionner les {allIds.length} résultats du filtre</button>
        )}
        {(ids.size > 0 || allFilter) && (
          <button onClick={clear} data-testid="selection-clear" className="rounded border border-slate-300 px-2 py-1 text-slate-500">Effacer la sélection</button>
        )}
        <span className="mx-1 h-4 w-px bg-slate-300" />
        <button onClick={exportCsv} disabled={count === 0} data-testid="selection-export-csv" className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 font-semibold text-slate-600 disabled:opacity-40"><span aria-hidden="true">↓</span> Exporter la sélection (CSV)</button>
        {extraActions}
      </div>
      <ScrollableTable className={className} ariaLabel={ariaLabel}>
        <SelCtx.Provider value={ctx}>{children}</SelCtx.Provider>
      </ScrollableTable>
    </div>
  );
}

export const SelectAllHeaderCheckbox = ({ label = "Sélectionner la page visible" }) => {
  const c = useContext(SelCtx);
  if (!c) return null;
  return <input type="checkbox" checked={c.allPage} ref={(el) => { if (el) el.indeterminate = c.indeterminate; }} onChange={c.togglePage} aria-label={label} data-testid="sel-all-page" data-no-drag />;
};

export const RowCheckbox = ({ id, label }) => {
  const c = useContext(SelCtx);
  if (!c) return null;
  return <input type="checkbox" checked={c.isSel(id)} onChange={() => c.toggleRow(id)} aria-label={label || `Sélectionner la ligne ${id}`} data-testid={`sel-row-${id}`} data-no-drag />;
};
